import { useState, useEffect } from 'react';
import { Users, Mail, Phone, Globe, Calendar, Star, Archive, MessageSquare } from 'lucide-react';
import { flooringBotDb } from '../lib/supabase';

interface Client {
  id: string;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  status: string;
  projectCount?: number;
  lastContact?: string;
  notes?: string;
  tags?: string[];
}

interface ClientsManifest {
  clients: Record<string, Client>;
  lastSync?: string;
}

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const { data, error } = await flooringBotDb.supabase
        .from('clients')
        .select(`
          *,
          conversations!inner(id, updated_at, status)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process clients with conversation counts
      const clientsMap = new Map<string, any>();

      data?.forEach((row: any) => {
        if (!clientsMap.has(row.id)) {
          clientsMap.set(row.id, {
            id: row.id,
            name: row.name,
            email: row.email || row.gmail,
            phone: row.phone,
            status: 'active',
            projectCount: 0,
            lastContact: row.created_at,
            tags: []
          });
        }

        // Count conversations
        if (row.conversations) {
          const client = clientsMap.get(row.id);
          client.projectCount++;

          // Update last contact if newer
          const convDate = new Date(row.conversations.updated_at);
          const lastDate = new Date(client.lastContact);
          if (convDate > lastDate) {
            client.lastContact = row.conversations.updated_at;
          }
        }
      });

      setClients(Array.from(clientsMap.values()));
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'lead': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'inactive': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'archived': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
    }
  };

  const filteredClients = clients.filter(client => {
    if (filter === 'all') return true;
    return client.status === filter;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    leads: clients.filter(c => c.status === 'lead').length,
    inactive: clients.filter(c => c.status === 'inactive').length
  };

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-400" />
            Clients
          </h1>
          <p className="text-gray-400">Client relationship management</p>
        </div>
        <button className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg flex items-center gap-2 transition">
          <Users className="w-4 h-4" />
          New Client
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Total Clients</div>
          <div className="text-2xl font-bold text-white">{stats.total}</div>
        </div>
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-green-500/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Active</div>
          <div className="text-2xl font-bold text-green-400">{stats.active}</div>
        </div>
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-blue-500/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Leads</div>
          <div className="text-2xl font-bold text-blue-400">{stats.leads}</div>
        </div>
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-gray-500/30 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Inactive</div>
          <div className="text-2xl font-bold text-gray-400">{stats.inactive}</div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm transition ${
            filter === 'all'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-cyan-500/30'
          }`}
        >
          All ({clients.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-3 py-1.5 rounded-lg text-sm transition ${
            filter === 'active'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-green-500/30'
          }`}
        >
          Active ({stats.active})
        </button>
        <button
          onClick={() => setFilter('lead')}
          className={`px-3 py-1.5 rounded-lg text-sm transition ${
            filter === 'lead'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-blue-500/30'
          }`}
        >
          Leads ({stats.leads})
        </button>
        <button
          onClick={() => setFilter('inactive')}
          className={`px-3 py-1.5 rounded-lg text-sm transition ${
            filter === 'inactive'
              ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-gray-500/30'
          }`}
        >
          Inactive ({stats.inactive})
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400">Loading clients...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className="bg-[#161b22]/30 backdrop-blur-xl backdrop-saturate-150 border border-[#30363d] rounded-lg p-5 hover:border-cyan-500/30 transition group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition">
                    {client.name}
                  </h3>
                  {client.company && client.company !== client.name && (
                    <p className="text-sm text-gray-400">{client.company}</p>
                  )}
                </div>
                <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(client.status)}`}>
                  {client.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                {client.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Mail className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="truncate">{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Phone className="w-3.5 h-3.5 text-green-400" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.website && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Globe className="w-3.5 h-3.5 text-violet-400" />
                    <a
                      href={client.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate hover:text-violet-400 transition"
                    >
                      {client.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>

              {client.tags && client.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {client.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-white/5 rounded text-xs text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-sm pt-3 border-t border-[#30363d]">
                <div className="flex items-center gap-3 text-gray-500">
                  {client.lastContact && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(client.lastContact).toLocaleDateString()}
                    </span>
                  )}
                  {client.projectCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {client.projectCount} {client.projectCount === 1 ? 'conversation' : 'conversations'}
                    </span>
                  )}
                </div>
                <button
                  className="p-1.5 hover:bg-white/5 rounded transition"
                  title="More options"
                >
                  <Archive className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          ))}

          {filteredClients.length === 0 && (
            <div className="col-span-3 text-center text-gray-500 py-12">
              {filter === 'all' ? 'No clients found' : `No ${filter} clients`}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
